/**
 * useContributionOptIn — RAELA community fine-tuning opt-in.
 *
 * Persists state in two places:
 *   1. localStorage (instant UI, offline-friendly)
 *   2. Server table `raela_training_optins` via the
 *      `contribution-opt-in` edge function (source of truth, used by
 *      RLS to gate contribution inserts).
 *
 * The first server-confirmed opt-in returns rewardJustGranted=true,
 * which the page surfaces as the "free month" toast.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

type State = {
  optedIn: boolean;
  optedInAt: string | null;
  rewardClaimedAt: string | null;
};

const KEY = (uid: string) => `wavlynq.raela_training_optin.${uid}`;
const EMPTY: State = { optedIn: false, optedInAt: null, rewardClaimedAt: null };

const read = (uid: string | undefined): State => {
  if (!uid || typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(KEY(uid));
    if (!raw) return EMPTY;
    return { ...EMPTY, ...(JSON.parse(raw) as Partial<State>) };
  } catch {
    return EMPTY;
  }
};

export const useContributionOptIn = () => {
  const { session } = useAuth();
  const uid = session?.user.id;
  const [state, setState] = useState<State>(() => read(uid));
  const [pending, setPending] = useState(false);

  // Hydrate from server on auth change
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!uid) {
        setState(EMPTY);
        return;
      }
      setState(read(uid));
      const { data } = await supabase
        .from("raela_training_optins")
        .select("opted_in_at, opted_out_at, reward_granted_at")
        .eq("user_id", uid)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        const next: State = {
          optedIn: !data.opted_out_at,
          optedInAt: data.opted_in_at,
          rewardClaimedAt: data.reward_granted_at,
        };
        window.localStorage.setItem(KEY(uid), JSON.stringify(next));
        setState(next);
      }
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const persist = useCallback(
    (next: State) => {
      if (!uid) return;
      window.localStorage.setItem(KEY(uid), JSON.stringify(next));
      setState(next);
    },
    [uid],
  );

  const optIn = useCallback(async (): Promise<{ rewardJustGranted: boolean }> => {
    if (!uid) return { rewardJustGranted: false };
    setPending(true);
    try {
      const { data, error } = await supabase.functions.invoke("contribution-opt-in", {
        body: { action: "opt_in" },
      });
      if (error) throw error;
      const s = data?.state;
      if (s) {
        persist({
          optedIn: !s.opted_out_at,
          optedInAt: s.opted_in_at,
          rewardClaimedAt: s.reward_granted_at,
        });
      }
      return { rewardJustGranted: Boolean(data?.rewardJustGranted) };
    } finally {
      setPending(false);
    }
  }, [uid, persist]);

  const optOut = useCallback(async () => {
    if (!uid) return;
    setPending(true);
    try {
      await supabase.functions.invoke("contribution-opt-in", {
        body: { action: "opt_out" },
      });
      persist({ ...state, optedIn: false });
    } finally {
      setPending(false);
    }
  }, [uid, state, persist]);

  return {
    ...state,
    pending,
    isAuthenticated: Boolean(uid),
    isFirstTimeReward:
      state.optedIn && state.rewardClaimedAt === state.optedInAt,
    optIn,
    optOut,
  };
};
